import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
   DashboardDataStatus,
   DashboardRiskReportTypeResponse,
   DashboardRiskSeverity,
   DashboardRiskSummaryResponse
} from '../../../services/response/user-property-dashboard-response';

import { DashboardCardComponent } from '../dashboard-card/dashboard-card.component';

@Component({
   selector: 'app-dashboard-risk-summary',
   standalone: true,
   imports: [CommonModule, DashboardCardComponent],
   templateUrl: './risk-summary.component.html',
   styleUrls: ['./risk-summary.component.scss']
})
export class RiskSummaryComponent {
   @Input() summary: DashboardRiskSummaryResponse | null = null;
   @Input() dataStatus: DashboardDataStatus | null = null;
   @Input() loading = false;
   @Input() error = false;
   @Input() startDate: string | null = null;
   @Input() endDate: string | null = null;
   @Input() periodLabel: string | null = null;

   @Output() viewTask = new EventEmitter<number>();
   @Output() viewReport = new EventEmitter<DashboardRiskReportTypeResponse>();

   expanded = false;

   get isMultiDayPeriod(): boolean {
      return !!this.startDate && !!this.endDate && this.startDate !== this.endDate;
   }

   get title(): string {
      const title = this.periodLabel
         ? `Risk Summary - ${this.periodLabel}`
         : 'Risk Summary';

      return this.isMultiDayPeriod ? `${title} (AVG)` : title;
   }

   get summaryText(): string | null {
      if (!this.summary) {
         return null;
      }

      return [
         `${this.formatCount(this.summary.riskCount)} ${
            this.summary.riskCount === 1 ? 'finding' : 'findings'
         }`,
         `${this.formatCount(this.summary.affectedRowCount)} affected ${
            this.summary.affectedRowCount === 1 ? 'row' : 'rows'
         }`,
         `${this.formatCount(this.summary.affectedTaskCount)} affected ${
            this.summary.affectedTaskCount === 1 ? 'task' : 'tasks'
         }`
      ].join(' · ');
   }

   get visibleReportTypes(): DashboardRiskReportTypeResponse[] {
      if (!this.summary) {
         return [];
      }

      const sortedReports = [...this.summary.reportTypes].sort(
         (a, b) =>
            this.getSeverityRank(b.highestSeverity) -
            this.getSeverityRank(a.highestSeverity)
      );

      return this.expanded ? sortedReports : sortedReports.slice(0, 5);
   }

   private getSeverityRank(severity: DashboardRiskSeverity | null): number {
      switch (severity) {
         case 'HIGH':
            return 4;

         case 'MEDIUM':
            return 3;

         case 'WARNING':
            return 2;

         case 'LOW':
            return 1;

         default:
            return 0;
      }
   }

   get hasMoreReports(): boolean {
      return (this.summary?.reportTypes.length ?? 0) > 5;
   }

   isFinancialReport(report: DashboardRiskReportTypeResponse): boolean {
      return report.financialRiskCount > 0;
   }

   isOperationalReport(report: DashboardRiskReportTypeResponse): boolean {
      return report.financialRiskCount === 0 && report.operationsRiskCount > 0;
   }

   formatCurrency(value: number | null | undefined): string {
      return new Intl.NumberFormat('en-AU', {
         style: 'currency',
         currency: 'AUD',
         minimumFractionDigits: 2,
         maximumFractionDigits: 2
      }).format(value ?? 0);
   }

   formatCount(value: number | null | undefined): string {
      return new Intl.NumberFormat('en-AU', {
         minimumFractionDigits: 0,
         maximumFractionDigits: this.isMultiDayPeriod ? 1 : 0
      }).format(value ?? 0);
   }

   formatOperationalRisks(count: number): string {
      return `${this.formatCount(count)} ${count === 1 ? 'risk' : 'risks'}`;
   }

   getSeverityClass(severity: DashboardRiskSeverity | null): string {
      switch (severity) {
         case 'HIGH':
            return 'risk-severity-high';

         case 'MEDIUM':
            return 'risk-severity-medium';

         case 'WARNING':
            return 'risk-severity-warning';

         case 'LOW':
            return 'risk-severity-low';

         default:
            return 'risk-severity-none';
      }
   }

   toggleExpanded(): void {
      this.expanded = !this.expanded;
   }

   onViewReport(report: DashboardRiskReportTypeResponse): void {
      if (this.isMultiDayPeriod) {
         this.viewReport.emit(report);
         return;
      }

      if (report.taskInstanceId !== null) {
         this.viewTask.emit(report.taskInstanceId);
      }
   }

   onViewTask(taskInstanceId: number | null): void {
      if (taskInstanceId === null) {
         return;
      }

      this.viewTask.emit(taskInstanceId);
   }
}
